import { GridColDef } from "@mui/x-data-grid"
import * as d3 from "d3"
import { NodeResult } from "@custom-types/modelRun"

export function calcDistribution(
  values: number[],
  thresholds: number,
  minVal: number,
  maxVal: number,
): [string[], number[]] {
  const sortedValues = values.slice().sort(function (a, b) {
    return a - b
  })

  const total_items = sortedValues.length
  if (!total_items) {
    return [[], []]
  }

  const hist_edges = []
  const bin_width = (maxVal - minVal) / thresholds
  for (let i = 0; i < thresholds; i++) {
    hist_edges.push(minVal + bin_width * i)
  }
  hist_edges.push(maxVal)

  const bin_fn = d3.bin().domain([minVal, maxVal]).thresholds(hist_edges)
  const bins = bin_fn(sortedValues)

  const xValues = []
  const yValues = []
  for (let i = 0; i < bins.length; i++) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    const xValue = (Math.round(hist_edges[i] * 100) / 100).toFixed(2)
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    const density = bins[i].length / total_items / bin_width
    xValues.push(xValue)
    yValues.push(density)
  }
  return [xValues, yValues]
}

export function quantileSorted(
  values: number[],
  percentileLower: number,
  percentileUpper: number,
): [number, number, number] {
  const sortedValues = values.slice().sort(function (a, b) {
    return a - b
  })

  const length = sortedValues.length
  if (!length) {
    return [0, 0, 0]
  }

  if (length < 2) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    return [sortedValues[0], sortedValues[0], sortedValues[0]]
  }

  return [
    d3.quantileSorted(sortedValues, percentileLower) || 0,
    d3.median(sortedValues) || 0,
    d3.quantileSorted(sortedValues, percentileUpper) || 0,
  ]
}

export const rawResultsColumns: GridColDef[] = [
  {
    field: "node_key",
    headerName: "Node",
    headerClassName: "table-header-cell",
    sortable: false,
    filterable: false,
    width: 280,
  },
  {
    field: "year",
    headerName: "Year",
    headerClassName: "table-header-cell",
    sortable: false,
    filterable: false,
    width: 80,
  },
  {
    field: "data",
    headerName: "Data",
    headerClassName: "table-header-cell",
    sortable: false,
    filterable: false,
    flex: 1,
    valueFormatter: (params) => (params?.value ? params?.value.toString() : ""),
  },
]

export const getMaterialTableColumns = () => [
  {
    accessorKey: "node_key",
    header: "Node",
    sortable: false,
    filterable: false,
    size: 280,
  },
  {
    accessorKey: "timestep",
    header: "Timestep",
    sortable: false,
    filterable: false,
    size: 80,
  },
  {
    accessorKey: "data",
    header: "Data",
    sortable: false,
    filterable: false,
    Cell: () => "Expand to see data",
  },
]

export type DataType =
  | "string"
  | "number"
  | "bigint"
  | "boolean"
  | "symbol"
  | "undefined"
  | "object"
  | "function"

export const checkDataType = <T extends string | number = number>(
  data: NodeResult<T>[] | undefined,
): DataType => {
  if (!data) {
    return "undefined"
  }
  let type: DataType = "number"
  for (const i of data) {
    if (i.data.length > 0) {
      type = typeof i.data[0]
    }
  }

  return type
}

export const color_tones: number[][] = [
  [250, 184, 26, 0.4],
  [0, 135, 148, 0.4],
  [181, 44, 79, 0.4],
  [146, 172, 84, 0.4],
  [226, 215, 55, 0.4],
  [126, 105, 176, 0.4],
  [89, 136, 116, 0.4],
  [91, 71, 112, 0.4],
  [228, 188, 94, 0.4],
  [135, 135, 135, 0.4],
  [223, 98, 87, 0.4],
  [97, 186, 195, 0.4],
  [166, 128, 41, 0.4],
  [75, 63, 87, 0.4],
  [250, 184, 26, 0.4],
  [0, 135, 148, 0.4],
  [181, 44, 79, 0.4],
  [146, 172, 84, 0.4],
  [226, 215, 55, 0.4],
  [126, 105, 176, 0.4],
  [89, 136, 116, 0.4],
]
export const color_tone2 = [80, 162, 210, 0.4]

export enum KernelEstimationFunction {
  Epanechnikov = "Epanechnikov",
  Gaussian = "Gaussian",
  Uniform = "Uniform",
  Triangular = "Triangular",
}

export const getKernelEsimationFunction = (functionName?: KernelEstimationFunction) => {
  if (!functionName) return null

  switch (functionName) {
    case KernelEstimationFunction.Epanechnikov:
      return kernelEpanechnikov()
    case KernelEstimationFunction.Gaussian:
      return kernelGaussian()
    case KernelEstimationFunction.Uniform:
      return kernelUniform()
    case KernelEstimationFunction.Triangular:
      return kernelTriangular()
    default:
      return null
  }
}

export function kernelEpanechnikov() {
  // k(x) = 3/4(1-(x^2)
  //curried
  return function _kernelEpanechnikov(data_value: number) {
    return Math.abs(data_value) <= 1 ? 0.75 * (1 - data_value * data_value) : 0
  }
}

export function kernelTriangular() {
  // k(x) = 1-abs(x) (x <= 1)

  //curried
  return function _kernelTriangular(data_value: number) {
    return Math.abs(data_value) <= 1 ? 1 - Math.abs(data_value) : 0
  }
}

export function kernelUniform() {
  // k(x) = 1/2 I

  //curried
  return function _kernelUniform(data_value: number) {
    return Math.abs(data_value) <= 1 ? 1 / 2 : 0
  }
}

export function kernelGaussian() {
  // k(x) = (1 / sqrt(2π)) * e^(-x^2/2)

  //curried
  return function _kernelGaussian(data_value: number) {
    const minus_x_sq_div_2 = -((data_value * data_value) / 2)
    const e_pow_minus_x_sq_div_2 = Math.exp(minus_x_sq_div_2)
    return (1 / Math.sqrt(2 * Math.PI)) * e_pow_minus_x_sq_div_2
  }
}

export function kurt(Ymean: number, Ydata: number[]) {
  //             n                       n
  // kurt = n * (Σ (Yi - Ymean(Y))^4 ) / (Σ (Yi - Ymean(Y)^2)^2 ) - 3
  //             i                       i

  const numerator = d3.sum(Ydata.map((y_i) => Math.pow(y_i - Ymean, 4)))
  const denominator = d3.sum(Ydata.map((y_i) => Math.pow(y_i - Math.pow(Ymean, 2), 2)))
  return Ydata.length * (numerator / denominator) - 3
}

export function kurt_def(tails: number) {
  if (tails == 3) return "mesokurtic (bell-shaped)"
  else if (tails < 3) return "platykurtic (flatter than normal, shorter tails)"
  else return "leptokurtic (peakier than normal, longer tails)"
}

export function skew_def(skew: number) {
  if (Math.abs(skew - 1) >= 0) return "(longer right tail)"
  else if (Math.abs(skew - 1) <= 0) return " (longer left tail)"
}

export function random_rgba(alpha = 0.4) {
  const o = Math.round,
    r = Math.random,
    s = 255
  return [o(r() * s), o(r() * s), o(r() * s), alpha]
}

export function lighter_rgba(rgba: number[], amount: number, alpha = 0.4) {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  return [(rgba[0] + amount) % 255, (rgba[1] + amount) % 255, (rgba[2] + amount) % 255, alpha]
}

export function to_rgba(rgba: number[]) {
  return "rgba(" + rgba[0] + "," + rgba[1] + "," + rgba[2] + "," + rgba[3] + ")"
}

export function pearsonCorrelationCoef(arr1: number[], arr2: number[]) {
  const mean1 = d3.mean(arr1) || 0
  const mean2 = d3.mean(arr2) || 0
  const length = arr1.length

  let sum = 0
  let sumSquares1 = 0
  let sumSquares2 = 0
  for (let i = 0; i < length; i++) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    sum += (arr1[i] - mean1) * (arr2[i] - mean2)
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    sumSquares1 += (arr1[i] - mean1) * (arr1[i] - mean1)
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    sumSquares2 += (arr2[i] - mean2) * (arr2[i] - mean2)
  }

  const B1Y = sum / sumSquares1
  const B0Y = mean2 - B1Y * mean1
  const B1X = sum / sumSquares2
  const B0X = mean1 - B1X * mean2

  return {
    coef: +(
      Math.round((sum / (Math.sqrt(sumSquares1) * Math.sqrt(sumSquares2))) * 1000) / 1000
    ).toFixed(3),
    B1Y,
    B0Y,
    B1X,
    B0X,
  }
}

export function findLagValue(modelNodes: string[]): number {
  const lagTitle = `_Lag`
  const lagNodes = modelNodes.filter((x: string) => x.includes(lagTitle))
  let lagShift = 0
  lagNodes.forEach((lagNode: string) => {
    const indexLag = lagNode.indexOf("_Lag")
    const res = +lagNode.substring(indexLag + 4, lagNode.length)
    if (res > lagShift) {
      lagShift = res
    }
  })

  return lagShift
}

export const smirnov = function (datasetA: number[], datasetB: number[]) {
  let maxi = -1
  let maxpos = -1
  let y1 = -1
  let y2 = -1

  for (let x = 0; x < datasetA.length; x++) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    const diff = Math.abs(datasetA[x] - datasetB[x])
    if (maxi < diff) {
      maxi = diff
      maxpos = x
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      y1 = datasetA[x]
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      y2 = datasetB[x]
    }
  }

  // https://en.wikipedia.org/wiki/Kolmogorov%E2%80%93Smirnov_test
  const alpha = 0.05
  const c_alpha = Math.sqrt(-Math.log(alpha / 2) * 0.5)
  const factor = Math.sqrt(
    (datasetA.length + datasetB.length) / (datasetA.length * datasetB.length),
  )

  const smirnov_at_5pct = c_alpha * factor
  return {
    suprenum: maxi,
    statistic: factor,
    p_value: smirnov_at_5pct,
    max_pos_x: maxpos,
    y1: y1,
    y2: y2,
  }
}

export const kde = function (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  xValues: any[],
  X: number[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  K: any,
  estimationBandwidth: number,
  bin_width: number,
  clamp_fn: ((i: number) => boolean) | null,
) {
  // https://faculty.washington.edu/yenchic/17Sp_403/Lec7-density.pdf
  // let x be randomly chosen point Xi be the ith item of the real sample
  //              n
  // PDE = 1/nh * Σ K( (x - Xi)/h )
  //             i=1
  // X=[....],
  // x, a random position in the range of X <-1-2-3-4->
  // Xi=ith observation in X
  // h=bandwidth
  // n = number of observations [for us, random equally spaced points coverign all of x-axis]
  // K = the kernel to estimate

  // this logic deals with scaling results
  // higher bandwidth = smoother, lower bandwidth = spikier

  const h = estimationBandwidth * bin_width //bandwidth TODO expose to user

  const n = X.length
  // add the min and max for the clamp to the X values
  const all_pn_x = xValues.map(function (x: string, i: number) {
    const adjusted_x = +x - -0.5 * bin_width //account for bar width
    let Ksumval = 0

    if (!clamp_fn || (clamp_fn && clamp_fn(i))) {
      X.map(function (X_i: number) {
        let v = 0

        v = K((X_i - adjusted_x) / h)
        Ksumval += v
      })
      return (1 / (n * h)) * Ksumval
    }

    return 0
  })

  // this logic again scales results
  return all_pn_x.map((x) => x)
}

export const toCDF = function (X: number[], bin_width: number) {
  let df = X
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  df = [X[0] * bin_width]
  const pdf_vals = X

  for (let i = 0; i < pdf_vals.length - 1; i += 1) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    df.push(Math.min(1.0, df[i] + pdf_vals[i + 1] * bin_width))
  }
  return df
}
